import React from 'react'
import { AppLink as Link } from '@components/AppLink'
import { observer } from 'mobx-react-lite'
import {
  Activity,
  BookOpen,
  CreditCard,
  FileText,
  Info,
  Lock,
  MessageSquare,
  Send,
  Shield,
} from 'lucide-react'
import { useStore } from '@store/StoreProvider'
import { openSiteWidget } from '@utils/openSiteWidget'
import './Footer.css'
import { SUPPORT_BOT_URL, TELEGRAM_NEWS_URL, supportBotLabel } from '@constants/support'
import { siteHref } from '@utils/appDomain'

const year = new Date().getFullYear()

const Footer: React.FC = observer(() => {
  const { i18n } = useStore()
  const t = (key: string, fallback: string) => {
    const value = i18n.t(key)
    return value === key ? fallback : value
  }
  const termsLabel = t('footer.terms', i18n.language === 'ru' ? 'Пользовательское соглашение' : 'Terms of Service')

  const openSupport = (e: React.MouseEvent) => {
    e.preventDefault()
    openSiteWidget()
  }

  return (
    <footer className="footer" role="contentinfo">
      <div className="footer__cols">
        <div className="footer__col footer__brand">
          <div className="footer__brandline">
            <img src="/assets/logo2.svg" alt="" height={26} />
            <b>
              Chat<i>X</i>
            </b>
          </div>
          <p className="footer__slogan">
            {t(
              'landing.mission',
              'Приватное общение с клиентами. Храним только то, что нужно для работы.',
            )}
          </p>
          <Link className="footer__status" to="/status">
            <span className="footer__status-pulse" aria-hidden />
            {t('footer.status_ok', 'Все системы работают')}
          </Link>
        </div>

        <div className="footer__col">
          <h5>{t('footer.help', 'Помощь')}</h5>
          <div className="footer__links">
            <a href="#support" onClick={openSupport}>
              <MessageSquare aria-hidden />
              {t('nav.help', 'Поддержка')}
            </a>
            <a href={SUPPORT_BOT_URL} target="_blank" rel="noopener noreferrer">
              <Send aria-hidden />
              {supportBotLabel((k) => i18n.t(k), i18n.language)}
            </a>
            <a href={TELEGRAM_NEWS_URL} target="_blank" rel="noopener noreferrer">
              <Send aria-hidden />
              {t('footer.news', 'Новости и релизы')}
            </a>
            <Link to="/knowledge-base">
              <BookOpen aria-hidden />
              {t('nav.knowledge_base', 'База знаний')}
            </Link>
          </div>
        </div>

        <div className="footer__col">
          <h5>{t('footer.service', 'Сервис')}</h5>
          <div className="footer__links">
            <a href={siteHref('/#pricing')}>
              <CreditCard aria-hidden />
              {t('footer.pricing', 'Тарифы и оплата')}
            </a>
            <Link to="/status">
              <Activity aria-hidden />
              {t('footer.service_status', 'Статус сервиса')}
            </Link>
            <a href={siteHref('/#privacy')}>
              <Lock aria-hidden />
              {t('footer.privacy', 'Приватность')}
            </a>
          </div>
        </div>

        <div className="footer__col">
          <h5>{t('footer.legal', 'Правовое')}</h5>
          <div className="footer__links">
            <Link to="/terms">
              <FileText aria-hidden />
              {termsLabel}
            </Link>
            <Link to="/policy">
              <Shield aria-hidden />
              {t('landing.footer.privacy_agreement', 'Политика')}
            </Link>
            <Link to="/terms#s8">
              <Info aria-hidden />
              {t('footer.rules', 'Правила')}
            </Link>
          </div>
        </div>
      </div>

      <div className="footer__bar">
        <span>© {year}</span>
        <span className="footer__disclaimer">
          {t(
            'landing.legal.disclaimer',
            'Запрещено использовать платформу для противоправной деятельности. Нарушение условий ведёт к блокировке без возврата средств.',
          )}
        </span>
        <span className="footer__legal">
          <Link to="/terms">{termsLabel}</Link>
          <Link to="/policy">{t('landing.footer.privacy_agreement', 'Политика')}</Link>
        </span>
      </div>
    </footer>
  )
})

export default Footer
